import { useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardText, Star } from "@phosphor-icons/react";
import AssessmentTab from "./AssessmentTab";
import ActionLogTab from "./ActionLogTab";
import type { FollowUpSummary } from "shared/types/candidate-follow-up";

interface FollowUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: number;
  candidateName: string;
  summary: FollowUpSummary | null;
  onSummaryChange?: () => void;
}

const FollowUpModal = ({
  isOpen,
  onClose,
  applicationId,
  candidateName,
  summary,
  onSummaryChange,
}: FollowUpModalProps) => {
  // Memoize callbacks so child components (AssessmentTab, ActionLogTab) receive
  // stable function references and don't re-render unnecessarily.
  const handleDialogOpenChange = useCallback(
    (open: boolean) => { if (!open) onClose(); },
    [onClose]
  );

  const handleRatingChange = useCallback(
    () => { onSummaryChange?.(); },
    [onSummaryChange]
  );

  const handleActionCountChange = useCallback(
    () => { onSummaryChange?.(); },
    [onSummaryChange]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ClipboardText
              size={20}
              weight="duotone"
              className="text-moss-primary"
            />
            Interne opvolging - {candidateName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="assessment" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="assessment" className="flex-1 gap-1.5">
              <Star size={14} weight="duotone" />
              Beoordeling
            </TabsTrigger>
            <TabsTrigger value="log" className="flex-1 gap-1.5">
              <ClipboardText size={14} weight="duotone" />
              Logboek
              {summary && summary.actionCount > 0 && (
                <span className="ml-1 rounded-full bg-neutral-200 px-1.5 text-xs">
                  {summary.actionCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assessment" className="mt-4">
            <AssessmentTab
              applicationId={applicationId}
              initialRating={summary?.rating ?? null}
              onRatingChange={handleRatingChange}
            />
          </TabsContent>

          <TabsContent value="log" className="mt-4">
            <ActionLogTab
              applicationId={applicationId}
              onActionCountChange={handleActionCountChange}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default FollowUpModal;
